import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Checkbox,
  Backdrop,
  CircularProgress,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import GavelIcon from "@mui/icons-material/Gavel";
import SendIcon from "@mui/icons-material/Send";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamIcon from "@mui/icons-material/Videocam";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkIcon from "@mui/icons-material/Link";
import TagIcon from "@mui/icons-material/Tag";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";

import ProcesosApi from "api/procesosApi";

// Formatear fecha a DD-MM-YYYY
const formatearFecha = (fecha) => {
  if (!fecha) return "-";
  const partes = fecha.split("-");
  if (partes.length === 3) {
    return `${partes[2]}-${partes[1]}-${partes[0]}`;
  }
  return fecha;
};

// Formatear hora a formato 12h con am/pm
const formatearHora = (hora) => {
  if (!hora) return "-";
  const partes = hora.split(":");
  if (partes.length >= 2) {
    let horas = parseInt(partes[0], 10);
    const minutos = partes[1];
    const periodo = horas >= 12 ? "pm" : "am";
    horas = horas % 12 || 12;
    return `${horas.toString().padStart(2, "0")}:${minutos} ${periodo}`;
  }
  return hora;
};

const ConvocarAudiencia = ({ open, onClose, audiencia, proceso, onSuccess }) => {
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [selectedDestinatarios, setSelectedDestinatarios] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participantes, setParticipantes] = useState(null);

  // Extraer participantes de la etapa Quórum (orden=1) cuando el modal se abre
  useEffect(() => {
    if (audiencia && open) {
      setError(null);
      setWarning(null);
      setSelectedDestinatarios({});
      setSelectAll(false);

      // Buscar la etapa de Quórum (orden=1)
      const etapas = audiencia.etapas || [];
      const etapaQuorum = etapas.find((e) => e.orden === 1);

      if (etapaQuorum && etapaQuorum.participantes) {
        setParticipantes(etapaQuorum.participantes);
      } else {
        setParticipantes(null);
        setError("No se encontraron participantes en la etapa de Quórum");
      }
    }
  }, [audiencia, open]);

  if (!audiencia) return null;

  const handleDestinatarioChange = (key, checked) => {
    setSelectedDestinatarios((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const newSelected = {};

    if (participantes) {
      // Deudor
      if (participantes.deudor && participantes.deudor.email) {
        newSelected["deudor"] = checked;
      }
      // Apoderado del deudor
      if (participantes.deudor_apoderado && participantes.deudor_apoderado.email) {
        newSelected["deudor_apoderado"] = checked;
      }
      // Acreedores
      if (participantes.acreedores && participantes.acreedores.length > 0) {
        participantes.acreedores.forEach((acreedor, index) => {
          if (acreedor.email) {
            newSelected[`acreedor_${index}`] = checked;
          }
          // Apoderado del acreedor
          if (acreedor.apoderado && acreedor.apoderado.email) {
            newSelected[`acreedor_${index}_apoderado`] = checked;
          }
        });
      }
    }

    setSelectedDestinatarios(newSelected);
  };

  const handleConvocar = async () => {
    // Validar que al menos un destinatario esté seleccionado
    const anySelected = Object.values(selectedDestinatarios).some((v) => v === true);
    if (!anySelected) {
      setError("Debe seleccionar al menos un destinatario");
      return;
    }

    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      const response = await ProcesosApi.convocarAudiencia(audiencia.id, {
        destinatarios_seleccionados: selectedDestinatarios,
        enviar_inmediatamente: true,
      });

      if (response.data.comunicacion_existente) {
        // Ya existe una convocatoria reciente
        const confirmar = window.confirm(
          "Ya se envió una convocatoria en las últimas 24 horas. ¿Desea enviar otra?"
        );
        if (confirmar) {
          // Forzar el envío
          const responseForced = await ProcesosApi.convocarAudiencia(audiencia.id, {
            destinatarios_seleccionados: selectedDestinatarios,
            enviar_inmediatamente: true,
            forzar_envio: true,
          });
          if (responseForced.data.envio?.success) {
            onSuccess && onSuccess();
            onClose();
          } else {
            setError("Ocurrió un error al enviar la convocatoria. Por favor, intente nuevamente.");
          }
        }
      } else if (response.data.envio?.success) {
        onSuccess && onSuccess();
        onClose();
      } else if (response.data.error) {
        setError("Ocurrió un error al enviar la convocatoria. Por favor, intente nuevamente.");
      } else {
        setWarning(response.data.mensaje || "Comunicación creada");
        onSuccess && onSuccess();
      }
    } catch (err) {
      console.error("Error al convocar audiencia:", err);
      setError(
        obtenerMensajeError(
          err,
          "Ocurrió un error al enviar la convocatoria. Por favor, intente nuevamente."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const renderParticipanteCompact = (participante, key, icon) => {
    if (!participante) return null;

    const tieneEmail = !!participante.email;
    const nombre = participante.nombre_completo || participante.nombre || "Sin nombre";

    return (
      <ListItem key={key} dense sx={{ py: 0.5, opacity: tieneEmail ? 1 : 0.5 }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Checkbox
            edge="start"
            size="small"
            checked={selectedDestinatarios[key] || false}
            onChange={(e) => handleDestinatarioChange(key, e.target.checked)}
            disabled={!tieneEmail}
          />
        </ListItemIcon>
        <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight="medium" noWrap>
              {nombre}
            </Typography>
          }
          secondary={
            <Box display="flex" alignItems="center" gap={0.5}>
              <EmailIcon sx={{ fontSize: 12 }} color={tieneEmail ? "primary" : "disabled"} />
              <Typography
                variant="caption"
                color={tieneEmail ? "textSecondary" : "error"}
                noWrap
                sx={{ maxWidth: 180 }}
              >
                {tieneEmail ? participante.email : "Sin email"}
              </Typography>
            </Box>
          }
        />
      </ListItem>
    );
  };

  const renderParticipantes = () => {
    if (!participantes) {
      return (
        <Alert severity="warning" sx={{ mt: 1 }}>
          No hay participantes disponibles para convocar
        </Alert>
      );
    }

    return (
      <Grid container spacing={2}>
        {/* Columna Izquierda: Deudor */}
        <Grid size={6}>
          <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 0.5 }}>
            Deudor
          </Typography>
          <List disablePadding dense>
            {participantes.deudor &&
              renderParticipanteCompact(
                participantes.deudor,
                "deudor",
                <PersonIcon color="primary" fontSize="small" />
              )}
            {!participantes.deudor && (
              <Typography variant="caption" color="textSecondary">
                Sin deudor registrado
              </Typography>
            )}
          </List>
        </Grid>

        {/* Columna Derecha: Apoderado del Deudor */}
        <Grid size={6}>
          <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 0.5 }}>
            Apoderado del Deudor
          </Typography>
          <List disablePadding dense>
            {participantes.deudor_apoderado &&
              renderParticipanteCompact(
                participantes.deudor_apoderado,
                "deudor_apoderado",
                <GavelIcon color="secondary" fontSize="small" />
              )}
            {!participantes.deudor_apoderado && (
              <Typography variant="caption" color="textSecondary">
                Sin apoderado
              </Typography>
            )}
          </List>
        </Grid>

        {/* Acreedores - En Grid de 2 columnas */}
        {participantes.acreedores && participantes.acreedores.length > 0 && (
          <Grid size={12}>
            <Typography
              variant="subtitle2"
              color="primary"
              fontWeight="bold"
              sx={{ mb: 0.5, mt: 1 }}
            >
              Acreedores ({participantes.acreedores.length})
            </Typography>
            <Grid container spacing={1}>
              {participantes.acreedores.map((acreedor, index) => (
                <React.Fragment key={`acreedor_group_${index}`}>
                  {/* Acreedor */}
                  <Grid size={acreedor.apoderado ? 6 : 12}>
                    <List disablePadding dense>
                      {renderParticipanteCompact(
                        acreedor,
                        `acreedor_${index}`,
                        <BusinessIcon color="info" fontSize="small" />
                      )}
                    </List>
                  </Grid>
                  {/* Apoderado del Acreedor */}
                  {acreedor.apoderado && (
                    <Grid size={6}>
                      <List disablePadding dense>
                        {renderParticipanteCompact(
                          acreedor.apoderado,
                          `acreedor_${index}_apoderado`,
                          <GavelIcon color="warning" fontSize="small" />
                        )}
                      </List>
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  // Contar destinatarios seleccionados
  const countSelected = Object.values(selectedDestinatarios).filter((v) => v === true).length;

  // Contar destinatarios disponibles (con email)
  const countAvailable = () => {
    let count = 0;
    if (participantes) {
      if (participantes.deudor?.email) count++;
      if (participantes.deudor_apoderado?.email) count++;
      participantes.acreedores?.forEach((a) => {
        if (a.email) count++;
        if (a.apoderado?.email) count++;
      });
    }
    return count;
  };

  return (
    <>
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            Convocar Participantes a Audiencia
          </Typography>
          {/* Info del Proceso */}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Proceso: {proceso?.identificador || "-"}
          </Typography>
          {/* Info de Audiencia compacta */}
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            <Chip
              icon={<EventIcon />}
              label={`#${audiencia?.consecutivo || "-"}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<EventIcon />}
              label={formatearFecha(audiencia?.fecha)}
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<AccessTimeIcon />}
              label={formatearHora(audiencia?.hora)}
              size="small"
              variant="outlined"
            />
            <Chip
              icon={
                audiencia?.modalidad?.toUpperCase() === "VIRTUAL" ? (
                  <VideocamIcon />
                ) : (
                  <LocationOnIcon />
                )
              }
              label={audiencia?.modalidad || "-"}
              size="small"
              color={audiencia?.modalidad?.toUpperCase() === "VIRTUAL" ? "info" : "default"}
              variant="outlined"
            />
            {/* Info de Zoom o Lugar según modalidad */}
            {audiencia?.modalidad?.toUpperCase() === "VIRTUAL" ? (
              <>
                <Chip
                  icon={<LinkIcon />}
                  label={audiencia?.enlace_zoom || "Sin enlace Zoom"}
                  size="small"
                  color={audiencia?.enlace_zoom ? "info" : "default"}
                  variant="outlined"
                  sx={{ maxWidth: 300 }}
                  title={audiencia?.enlace_zoom || "Sin enlace Zoom"}
                />
                <Chip
                  icon={<TagIcon />}
                  label={audiencia?.id_zoom ? `ID: ${audiencia.id_zoom}` : "Sin ID Zoom"}
                  size="small"
                  color={audiencia?.id_zoom ? "info" : "default"}
                  variant="outlined"
                />
              </>
            ) : (
              <Chip
                icon={<LocationOnIcon />}
                label={audiencia?.lugar || "Sin lugar definido"}
                size="small"
                variant="outlined"
                sx={{ maxWidth: 300 }}
                title={audiencia?.lugar || "Sin lugar definido"}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          {/* Selector de Todos */}
          <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight="medium">
              Seleccionar Destinatarios
            </Typography>
            <Box display="flex" alignItems="center">
              <Checkbox
                size="small"
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
                disabled={countAvailable() === 0}
              />
              <Typography variant="body2" color="textSecondary">
                Todos ({countSelected}/{countAvailable()})
              </Typography>
            </Box>
          </SuiBox>

          {/* Alertas */}
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {warning && (
            <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setWarning(null)}>
              {warning}
            </Alert>
          )}

          {/* Lista de Participantes */}
          {renderParticipantes()}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <SuiButton onClick={onClose} buttonColor="secondary" variant="outlined" size="small">
            Cancelar
          </SuiButton>
          <SuiButton
            onClick={handleConvocar}
            buttonColor="info"
            variant="gradient"
            size="small"
            disabled={loading || countSelected === 0}
            startIcon={<SendIcon />}
          >
            {loading ? "Enviando..." : `Convocar (${countSelected})`}
          </SuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

ConvocarAudiencia.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  audiencia: PropTypes.object,
  proceso: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default ConvocarAudiencia;
